import React, { useEffect, useState } from "react";
import { ChevronRight, Inbox, UserPlus, UserMinus, Wallet, UserX, IdCard, CalendarDays, CalendarX, Monitor, RefreshCw, Sparkles, Ticket, Star, Video } from "lucide-react";
import { api } from "../console/api";
import { Groupe, Ligne, TitreGroupe, Badge } from "../espace/ui";
import { francs, iso, jourLisible } from "./outils";

const COURTS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const jourCourt = (j) => {
  const auj = iso(new Date());
  if (j === auj) return "Auj.";
  const d = new Date(`${auj}T12:00:00`); d.setDate(d.getDate() + 1);
  if (j === iso(d)) return "Demain";
  const x = new Date(`${j}T12:00:00`);
  return `${COURTS[x.getDay()]} ${x.getDate()}.${x.getMonth() + 1}`;
};

const Chiffre = ({ valeur, label, ton }) => (
  <div className="rounded-2xl bg-white px-3 py-3" style={{ border: "1px solid var(--border)" }}>
    <div className="text-[22px] font-semibold tabular-nums leading-tight" style={ton ? { color: ton } : undefined}>{valeur}</div>
    <div className="text-[11.5px] text-[var(--muted)] leading-tight mt-0.5">{label}</div>
  </div>
);

/* Le tableau de bord de poche : ce qu'il faut faire, la journée, l'argent, l'école en
   chiffres. Chaque ligne mène là où l'on agit. */
export const TableauBord = ({ resume, aller, moi }) => {
  const [s, setS] = useState(null);
  const [jour, setJour] = useState(null);
  const [appli, setAppli] = useState(null);
  const [ev, setEv] = useState(null);
  const [listes, setListes] = useState(null);
  useEffect(() => {
    api("/mobile/evenements").then(setEv).catch(() => {});
    api("/mobile/listes").then(setListes).catch(() => {});
    api("/stats").then(setS).catch(() => {});
    api(`/mobile/jour?le=${iso(new Date())}`).then(setJour).catch(() => {});
    api("/application").then(setAppli).catch(() => {});
  }, []);

  const r = resume || {};
  const aFaire = [
    { n: r.nouvelles_inscriptions, Icone: UserPlus, couleur: "#2563EB", titre: "Nouvelles inscriptions", aide: "Vérifier, appeler la famille", aller: ["plus", { ecran: "alertes", groupe: "nouvelles_inscriptions" }] },
    { n: r.absences, Icone: CalendarX, couleur: "#F59E0B", titre: "Absences annoncées", aide: "Déjà marquées sur les listes de présence", aller: ["plus", { ecran: "alertes", groupe: "absences" }] },
    { n: r.desinscriptions, Icone: UserMinus, couleur: "#B45309", titre: "Désinscriptions", aide: "Confirmer, prévenir si besoin", aller: ["plus", { ecran: "alertes", groupe: "desinscriptions" }] },
    { n: r.reservations, Icone: Ticket, couleur: "#9333EA", titre: "Réservations spectacle", aide: "Nouvelles places réservées", aller: ["plus", { ecran: "alertes", groupe: "reservations_spectacle" }] },
    { n: r.messages, Icone: Inbox, couleur: "#00A8AF", titre: "Messages à traiter", aide: "Formulaire de contact, demandes", aller: ["messages"] },
    { n: r.impayes, Icone: Wallet, couleur: "#F59E0B", titre: "Paiements en retard", aide: "Relancer d'un tap", aller: ["plus", { ecran: "argent" }] },
    { n: r.profs_sans_remplacant, Icone: UserX, couleur: "#DC2626", titre: "Absences de profs sans remplaçant", aide: "Désigner qui remplace", aller: ["plus", { ecran: "profs" }] },
    // Vacances annoncées avec un remplaçant externe : l'appeler, puis « Remplaçant confirmé ».
    { n: r.profs_remplacant_a_confirmer, Icone: UserX, couleur: "#D97706", titre: "Remplaçants externes à confirmer", aide: "Appeler, puis « Remplaçant confirmé »", aller: ["plus", { ecran: "profs" }] },
    { n: r.avs_manquants, Icone: IdCard, couleur: "#64748B", titre: "Numéros AVS manquants", aide: "Voir les élèves concernés", aller: ["plus", { ecran: "alertes", groupe: "avs_manquants" }] },
  ].filter((x) => x.n > 0);

  const sc = s ? s.argent[`s${s.semestre_courant}`] : null;
  const attendus = jour ? jour.cours.reduce((n, c) => n + Math.max(0, c.attendus), 0) : 0;

  const heure = new Date().getHours();
  const salut = heure < 5 || heure >= 18 ? "Bonsoir" : "Bonjour";
  const mesCours = (moi?.mes_cours || []).filter((c) => !c.je_suis_absent).slice(0, 4);
  return (
    <div className="space-y-6" data-testid="ecole-tableau">
      <div>
        <h1 className="font-display text-[28px] leading-tight" data-testid="ecole-salut">
          {salut}{moi?.prenom ? ` ${moi.prenom}` : ""} 👋
        </h1>
        <p className="text-[14px] text-[var(--muted)] mt-1 first-letter:uppercase">{jourLisible(iso(new Date()))}</p>
      </div>

      {mesCours.length > 0 && (
        <section>
          <TitreGroupe>Mes prochains cours</TitreGroupe>
          <Groupe testId="ecole-mes-cours">
            {mesCours.map((c) => (
              <button key={`${c.date}-${c.cours_id}`} onClick={() => aller("journee", { cours: c.cours_id, le: c.date })}
                className="w-full flex gap-3.5 px-4 py-3 text-left active:bg-[#F1F5F9]" data-testid={`ecole-mon-cours-${c.cours_id}`}>
                <div className="w-[64px] shrink-0 self-start rounded-2xl text-center py-2" style={{ background: "#F3E8FF" }}>
                  <div className="text-[10.5px] font-bold tracking-[0.04em] uppercase" style={{ color: "#9333EA" }}>
                    {jourCourt(c.date)}
                  </div>
                  <div className="text-[15px] font-semibold tabular-nums leading-tight">{c.debut}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold leading-snug">{c.cours}</div>
                  <div className="text-[12.5px] text-[var(--muted)] mt-0.5">
                    {[c.salle ? `salle ${c.salle}` : "", c.note].filter(Boolean).join(" · ")}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    <Badge couleur={c.eleves ? "#0F1B2D" : "#DC2626"}>{c.eleves ? `${c.eleves} élève${c.eleves > 1 ? "s" : ""}` : "aucun élève"}</Badge>
                    {c.absents > 0 && <Badge couleur="#F59E0B">{c.absents} absent{c.absents > 1 ? "s" : ""}</Badge>}
                    {c.appel_fait && <Badge couleur="#16A34A">Appel fait ✓</Badge>}
                  </div>
                </div>
                <ChevronRight size={18} className="self-center shrink-0" style={{ color: "#C4C9D0" }} />
              </button>
            ))}
          </Groupe>
        </section>
      )}

      {(() => {
        // Ce qui arrive à l'école : le stage (en cours ou prochain), le prochain workshop
        // heels, les auditions et les spectacles. Un tap ouvre la liste des inscrits.
        const lignes = [];
        (ev?.stages || []).slice(0, 2).forEach((st) => lignes.push({
          cle: st.id, Icone: Sparkles, couleur: "#F59E0B", titre: st.nom,
          sous: `${st.dates} · ${st.participants.length} inscrit${st.participants.length > 1 ? "s" : ""}`,
          badge: st.en_cours ? "En cours" : "", aller: { ecran: "evenements", id: st.id } }));
        const ws = ev?.workshops?.seances?.[0];
        if (ws) lignes.push({ cle: ws.id, Icone: ws.video ? Video : Sparkles, couleur: "#EC4899", titre: `Workshop heels · ${jourLisible(ws.date)}`,
          sous: `${ws.debut}–${ws.fin} · ${ws.participants.length} inscrite${ws.participants.length > 1 ? "s" : ""}`, aller: { ecran: "evenements", id: ws.id } });
        (listes?.auditions || []).slice(0, 1).forEach((a) => lignes.push({ cle: `a-${a.id}`, Icone: Star, couleur: "#2563EB", titre: `Audition · ${a.nom}`,
          sous: `${a.nb_inscrits} inscrit${a.nb_inscrits > 1 ? "s" : ""}`, aller: { ecran: "evenements", id: a.id, type: "auditions" } }));
        (listes?.spectacles || []).slice(0, 1).forEach((sp) => lignes.push({ cle: `s-${sp.id}`, Icone: Ticket, couleur: "#9333EA", titre: sp.nom,
          sous: [sp.date ? jourLisible(sp.date) : sp.quand, sp.nb_personnes ? `${sp.nb_personnes} places réservées` : ""].filter(Boolean).join(" · "),
          aller: { ecran: "evenements", id: sp.id, type: "spectacles" } }));
        if (!lignes.length) return null;
        return (
          <section>
            <TitreGroupe action={<button onClick={() => aller("plus", { ecran: "evenements" })} className="text-[13px] font-medium" style={{ color: "var(--primary)" }}>Tout voir →</button>}>
              À venir à l'école
            </TitreGroupe>
            <Groupe testId="ecole-a-venir">
              {lignes.map((l) => (
                <Ligne key={l.cle} Icone={l.Icone} couleur={l.couleur} titre={l.titre} sousTitre={l.sous}
                  badge={l.badge ? <Badge couleur="#16A34A">{l.badge}</Badge> : null}
                  onClick={() => aller("plus", l.aller)} testId={`ecole-a-venir-${l.cle}`} />
              ))}
            </Groupe>
          </section>
        );
      })()}

      <section>
        <TitreGroupe>À faire</TitreGroupe>
        <Groupe testId="ecole-a-faire">
          {!resume ? <p className="px-4 py-3 text-sm text-[var(--muted)]">Chargement…</p>
            : aFaire.length === 0 ? <p className="px-4 py-4 text-[15px]">Rien d'urgent. 👌</p>
              : aFaire.map((x) => (
                <Ligne key={x.titre} Icone={x.Icone} couleur={x.couleur} titre={x.titre} sousTitre={x.aide}
                  badge={<Badge couleur={x.couleur}>{x.n}</Badge>} onClick={() => aller(...x.aller)}
                  testId={`ecole-a-faire-${x.aller[1]?.groupe || x.aller[1]?.ecran || x.aller[0]}`} />
              ))}
        </Groupe>
      </section>

      <section>
        <TitreGroupe>Aujourd'hui</TitreGroupe>
        <button onClick={() => aller("journee")} className="w-full text-left rounded-[22px] p-5 text-white active:opacity-90"
          style={{ background: "linear-gradient(140deg,#0F1B2D 0%,#123243 60%,#00A8AF 100%)" }} data-testid="ecole-tableau-jour">
          <div className="flex items-center justify-between">
            <div className="text-[12px] uppercase tracking-[0.14em] font-semibold capitalize" style={{ color: "#66EAF0" }}>
              {jourLisible(iso(new Date()))}
            </div>
            <ChevronRight size={18} />
          </div>
          {!jour ? <RefreshCw className="animate-spin mt-3" size={18} />
            : jour.ferme ? <div className="text-[18px] font-semibold mt-2">École fermée aujourd'hui</div>
              : (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {[[jour.cours.length, "cours"], [attendus, "élèves attendus"], [jour.essais.length, "essais"]].map(([v, l]) => (
                    <div key={l}><div className="text-[24px] font-semibold tabular-nums leading-tight">{v}</div>
                      <div className="text-[11.5px]" style={{ color: "rgba(255,255,255,0.75)" }}>{l}</div></div>
                  ))}
                </div>
              )}
          {jour?.cours.some((c) => c.profs_absents.length) && (
            <div className="text-[12.5px] mt-3" style={{ color: "#FDE68A" }}>⚠ Un prof est absent aujourd'hui</div>
          )}
        </button>
      </section>

      {sc && (
        <section>
          <TitreGroupe action={<button onClick={() => aller("plus", { ecran: "argent" })} className="text-[13px] font-medium" style={{ color: "var(--primary)" }}>Impayés →</button>}>
            Argent · semestre {s.semestre_courant}
          </TitreGroupe>
          <div className="grid grid-cols-3 gap-2">
            <Chiffre valeur={francs(sc.du + s.argent.stages.du)} label="à facturer (cours + stages)" />
            <Chiffre valeur={francs(sc.encaisse + s.argent.stages.encaisse)} label="encaissé" ton="#15803D" />
            <Chiffre valeur={francs(sc.reste + s.argent.stages.reste)} label="reste à encaisser" ton="#B45309" />
          </div>
          {(sc.impayees > 0 || sc.a_envoyer > 0) && (
            <p className="text-[12.5px] text-[var(--muted)] mt-2 px-1">
              {sc.impayees} facture{sc.impayees > 1 ? "s" : ""} impayée{sc.impayees > 1 ? "s" : ""}
              {sc.a_envoyer ? ` · ${sc.a_envoyer} à envoyer (sur ordinateur)` : ""}
            </p>
          )}
        </section>
      )}

      {s && (
        <section>
          <TitreGroupe>L'école</TitreGroupe>
          <div className="grid grid-cols-3 gap-2">
            <Chiffre valeur={s.eleves} label="élèves" />
            <Chiffre valeur={s.familles} label="familles" />
            <Chiffre valeur={`${s.remplissage} %`} label="remplissage" ton={s.remplissage < 40 ? "#B45309" : "#15803D"} />
            <Chiffre valeur={s.essais_a_venir} label="essais à venir" />
            <Chiffre valeur={`${s.taux_conversion} %`} label="essais transformés" />
            <Chiffre valeur={`${s.places_occupees}/${s.places_offertes}`} label="places occupées" />
          </div>
        </section>
      )}

      {s && (s.cours_sous_effectif || []).length > 0 && (
        <section>
          <TitreGroupe>Cours à moins de 4 élèves</TitreGroupe>
          <Groupe>
            {s.cours_sous_effectif.slice(0, 8).map((c) => (
              <button key={c.id} onClick={() => aller("journee", { cours: c.id })}
                className="w-full text-left flex items-center justify-between gap-3 px-4 py-3 active:bg-[#F1F5F9]">
                <span className="min-w-0">
                  <span className="block text-[14.5px] truncate">{c.nom}</span>
                  <span className="block text-[12px] text-[var(--muted)]">{c.jour} {c.debut}</span>
                </span>
                <Badge couleur="#F59E0B">{c.nb ?? c.inscrits ?? 0}</Badge>
              </button>
            ))}
          </Groupe>
        </section>
      )}

      {appli && (
        <section>
          <TitreGroupe>Application des familles</TitreGroupe>
          <div className="grid grid-cols-3 gap-2">
            <Chiffre valeur={appli.resume?.comptes ?? appli.comptes ?? 0} label="comptes" />
            <Chiffre valeur={appli.resume?.installees ?? appli.installees ?? 0} label="installée" />
            <Chiffre valeur={appli.resume?.absences_a_venir ?? appli.absences_a_venir ?? 0} label="absences annoncées" />
          </div>
        </section>
      )}

      <Groupe>
        <Ligne Icone={CalendarDays} couleur="#0EA5E9" titre="La journée en détail" onClick={() => aller("journee")} />
        <Ligne Icone={Monitor} couleur="#0F1B2D" titre="Ouvrir la console complète" sousTitre="Toutes les fonctions (mieux sur ordinateur)"
          href="/console" testId="ecole-console-complete-ligne" />
      </Groupe>
    </div>
  );
};
